import type { components, paths } from './schema';

type S = components['schemas'];

export type Key = S['Key'];
export type ApiToken = S['ApiToken'];
export type CreatedApiToken = S['CreatedApiToken'];
export type AuditEvent = S['AuditEvent'];
export type UsageLine = S['UsageLine'];
export type Me = S['Me'];
export type Billing = S['Billing'];
export type BillingSession = S['BillingSession'];
export type Container = S['Container'];
/** A version a rotation retired, and the time after which rypt destroys it. */
export type RetiringVersion = S['RetiringVersion'];
export type Firewall = S['Firewall'];
export type ErrorEnvelope = S['Error'];
/** `PATCH /v1/api-tokens/{id}`'s answer: the key, and its container's permission chain head. */
export type UpdatedApiToken = S['UpdatedApiToken'];
/** The latest entry of a container's permission chain: its position and its hash. */
export type ChainHead = S['ChainHead'];
/** `GET /v1/containers/{id}/permission-chain`. */
export type PermissionChain = S['PermissionChain'];

/** Something an API token may do with a key it may act on, or, for `create`, in its container. */
export type Action = S['AllowedActions'][number];

/**
 * Every action, in the order the dashboard lists them: read, which lists keys and reads
 * a key, its audit log and usage; the two direct operations, the three envelope ones and
 * rotate, which use a key; then create, rename, delete, restore and retire, which manage
 * keys. The API returns them sorted by name instead; this is only the order they are
 * drawn in. A Record, so an action added to the spec is a compile error until it has a
 * place here.
 */
const ACTION_ORDER = {
  read: 0,
  encrypt: 1,
  decrypt: 2,
  wrap: 3,
  unwrap: 4,
  rewrap: 5,
  rotate: 6,
  create: 7,
  rename: 8,
  delete: 9,
  restore: 10,
  retire: 11,
} satisfies Record<Action, number>;
export const ACTIONS: readonly Action[] = (Object.keys(ACTION_ORDER) as Action[]).sort(
  (a, b) => ACTION_ORDER[a] - ACTION_ORDER[b],
);
/** Actions in the order the dashboard lists them, without repeats. */
export const inActionOrder = (actions: readonly Action[]): Action[] =>
  ACTIONS.filter((action) => actions.includes(action));

/**
 * The three groups the actions are drawn in: reading keys, using them, and managing them.
 * `read` has a group of its own: it touches no key material and changes nothing, so it
 * is neither a use of a key nor a change to one, and its group carries the note on what
 * an API token without it cannot do.
 */
export type ActionGroup = 'read' | 'use' | 'manage';
/** Each action's group. A Record for the reason ACTION_ORDER is one. */
const ACTION_GROUP = {
  read: 'read',
  encrypt: 'use',
  decrypt: 'use',
  wrap: 'use',
  unwrap: 'use',
  rewrap: 'use',
  rotate: 'use',
  create: 'manage',
  rename: 'manage',
  delete: 'manage',
  restore: 'manage',
  retire: 'manage',
} as const satisfies Record<Action, ActionGroup>;
/** The groups in the order they are drawn. */
export const ACTION_GROUPS: readonly ActionGroup[] = ['read', 'use', 'manage'];
/** A group's actions, in the order the dashboard lists them. */
export const actionsIn = (group: ActionGroup): Action[] =>
  ACTIONS.filter((action) => ACTION_GROUP[action] === group);

/** All 46 codes, straight off the spec. Adding a 47th to the API is a compile error here. */
export type ErrorCode = ErrorEnvelope['error'];

export type Tier = Key['tier'];
export type Role = Me['role'];
export type AuditOp = AuditEvent['op'];
/** `active`, or `pending_deletion`: deleted, refusing every operation, and not yet destroyed. */
export type KeyState = Key['state'];

/**
 * What the UI is allowed to hold after minting an API token.
 *
 * `token` is the plaintext credential, returned exactly once and never recoverable.
 * `prefix` is withheld by the API from every other endpoint on purpose: the per-key rate
 * limit is charged on the prefix lookup, before the Argon2 verification, so a prefix
 * alone drains that key's budget. Neither belongs in a cache, in storage, or in a render
 * tree that outlives the reveal dialog, so the type makes them unreachable.
 */
export type MintedApiToken = Omit<CreatedApiToken, 'token' | 'prefix'>;
/** The body of `PATCH /v1/api-tokens/{id}`: absent keeps a restriction, `null` lifts it. */
export type ApiTokenRestrictions = NonNullable<
  paths['/v1/api-tokens/{id}']['patch']['requestBody']
>['content']['application/json'];

/*
 * The tier table. Every value below mirrors rypt-core's tier.rs, which is the authority:
 * these copies exist so the dashboard can say what a tier is before the API is asked.
 * Where money is concerned only the API's billing estimate is binding; these draw the
 * captions and the price list. Each is a Record<Tier, …>, so a tier added to the spec is
 * a compile error until every table has a row for it.
 */

/**
 * Versions each tier's fee includes. Mirrors rypt-core's `Tier::included_versions`, and
 * the key object carries its own as `included_versions`, which is what a key's page uses;
 * this draws the price list. A paid key may keep any number of versions, and each beyond
 * these is billed by the day from December 2026. A free key keeps exactly one, so each of
 * its rotations retires the one before.
 */
export const INCLUDED_VERSIONS: Record<Tier, number> = { free: 1, wrapped: 1, software: 3, hsm: 2 };

/**
 * Price of one version beyond those included for a whole month, in US cents, billed by
 * the UTC day from December 2026 until the version is destroyed, retiring days included.
 * Mirrors rypt-core's tier table, and the key object carries its own as
 * `extra_version_monthly_cents`; this draws the price list. 0 on free, which keeps one.
 */
export const EXTRA_VERSION_MONTHLY_CENTS: Record<Tier, number> = { free: 0, wrapped: 5, software: 25, hsm: 200 };

/**
 * Days a retired version keeps decrypting, unwrapping and rewrapping before rypt destroys
 * it. Mirrors the API's retirement period. Only the rotate and retire dialogs use it, to
 * say when before the click; afterwards the key's own `retiring_versions` carry the time.
 */
export const RETIRING_DAYS = 7;

/**
 * The most versions a key may have retiring at once, counting those whose
 * `destroy_after` is still ahead. Mirrors the API's MAX_RETIRING_VERSIONS: a rotation or
 * a retirement by hand that would take the key past it is refused with 409
 * too_many_retiring_versions.
 */
export const MAX_RETIRING_VERSIONS = 10;

/**
 * Operations per calendar month (UTC) per tier. Mirrors rypt-core's tier table. On free
 * a hard limit on encrypt, wrap and rewrap, past which decrypt and unwrap go on; on the
 * paid tiers it is the included allowance of a key that exists all month, and past it is
 * overage. From October 2026 a paid key that exists for fewer days
 * gets the same share of it as of the fee, rounded down: the usage report's `allowance`.
 */
export const MONTHLY_CAP: Record<Tier, number> = {
  free: 10_000,
  wrapped: 50_000,
  software: 100_000,
  hsm: 250_000,
};

/**
 * What a key does at its cap. `overage`: operations past it are allowed and recorded as
 * overage. `refused`: encrypt, wrap, rewrap and rotate are refused with 429
 * ops_cap_reached until the month ends (UTC), and decrypt and unwrap go on, counted and
 * never billed, so data can always be read back.
 */
export const OVER_CAP: Record<Tier, 'refused' | 'overage'> = {
  free: 'refused',
  wrapped: 'overage',
  software: 'overage',
  hsm: 'overage',
};

/**
 * Display label, chrome only: the wire value is what a developer types. hsm's is HSM, the
 * name developers already use for it, rather than a gloss on that name.
 */
export const TIER_LABEL: Record<Tier, string> = {
  free: 'Free',
  wrapped: 'Wrapped',
  software: 'Software',
  hsm: 'HSM',
};

/**
 * Fee per key-month, one paid key for a whole month, in US cents. Mirrors rypt-core's
 * tier table. From October 2026 a paid key is billed by the day: this is split evenly
 * across the days of the UTC month, and the key pays for every day it exists in, through
 * the day its deletion is requested.
 */
export const MONTHLY_FEE_CENTS: Record<Tier, number> = { free: 0, wrapped: 300, software: 1000, hsm: 3500 };

/**
 * Price of one operation past the allowance, in millicents: thousandths of a cent, because
 * the price is a fraction of one. Mirrors rypt-core's tier table. 0 on free, which refuses
 * new ciphertext rather than charging.
 */
export const OVERAGE_MILLICENTS_PER_OP: Record<Tier, number> = { free: 0, wrapped: 1, software: 2, hsm: 5 };

/**
 * Every tier, in the order a person chooses between them: the key order of a
 * Record<Tier, …>, so it cannot silently leave one out the way a hand-written list can.
 */
export const TIERS = Object.keys(MONTHLY_CAP) as readonly Tier[];

/** A paid tier is one with a fee. Mirrors rypt-core's `Tier::is_paid`. */
export const isPaidTier = (tier: Tier): boolean => MONTHLY_FEE_CENTS[tier] > 0;

/**
 * Good standing: paid keys can be created, and encrypt, wrap, rewrap and rotate on them
 * work.
 * `past_due` counts, because Stripe is still retrying the payment and rypt stops nothing
 * while it does. `unpaid` does not: the retries have run out, and until the open invoice
 * is paid it stops what `lapsed` stops. Any status word the API adds later fails closed.
 */
export const inGoodStanding = (status: Billing['status']): boolean => status === 'active' || status === 'past_due';
